// MyPocket.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
// import MainLayout from '../../layouts/MainLayout';
import './MyWallet.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faWallet } from '@fortawesome/free-solid-svg-icons';

const MyPocket = () => {
  const authState = useSelector(state => state.authReducer);
  const [userId, setUserId] = useState(authState.user._id);
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();
  const [pocketBalanceAmount, setPocketBalanceAmount] = useState(0.00);
  const [pocketLastUpdated, setPocketLastUpdated] = useState();
  const [pocketLastUpdatedRemarks, setPocketLastUpdatedRemarks] = useState('');

  const [pocketData, setPocketData] = useState([]);
  const [selectedPocketId, setSelectedPocketId] = useState([]);

  //PocketBalance
  const fetchPocketBalance = useCallback(() => {
    const config = {
      url: "/pocketbalance/my",
      method: "post",
      headers: { Authorization: authState.token },
      data: {userId: userId}
    };

    fetchData(config, { showSuccessToast: false })
        .then(response => {
            // console.log("Pocket Balance Response:", response);

            // Ensure the response contains the expected structure
            if (response.status && response.pocketBalance) {
                const { balanceAmount, lastTransactionDate, remarks } = response.pocketBalance;

                // Update the state with the fetched data
                setPocketBalanceAmount(balanceAmount || 0.0);
                setPocketLastUpdated(lastTransactionDate || null);
                setPocketLastUpdatedRemarks(remarks || "No remarks available");
            } else {
                console.error("Invalid response format:", response);
                setPocketBalanceAmount(0.0);
                setPocketLastUpdated(null);
                setPocketLastUpdatedRemarks("Record Not Available!");
            }
        })
        .catch(error => {
            console.error("Error fetching pocket balance:", error);
            setPocketBalanceAmount(0.0);
            setPocketLastUpdated(null);
            setPocketLastUpdatedRemarks("Error fetching data");
        });
    }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchPocketBalance();
  }, [authState.isLoggedIn, fetchPocketBalance]);

  //Pocket Transactions
  const fetchPocket = useCallback(() => {
    const config = {
      url: "/pocket/myall",
      method: "post",
      headers: { Authorization: authState.token },
      data: {userId: userId}
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        // console.log("Response:", response);
        if (response && Array.isArray(response)) {
          setPocketData(response);
        } else {
          console.error("Invalid response format: Pocket transaction data not found in response");
        }
      })
      .catch(error => {
        console.error("Error fetching pockets:", error);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchPocket();
  }, [authState.isLoggedIn, fetchPocket]);

  const sortedPocketData = [...pocketData].sort((a, b) => new Date(b.dateTransaction) - new Date(a.dateTransaction));

  const handlePocketView = (_id) => {
    setSelectedPocketId(prevId => (prevId === _id ? null : _id));
  };

  const handleTopupClick = (userId) => {
    navigate('/wallet-pockettransfer', { state: { userId: userId } });
  };

  const formatDateTime = (date) => {
    const options = {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="bg-black text-white text-sm">
      <header
          className="
              sticky top-0 z-50 
              bg-black 
              px-4 py-3 
              flex flex-col items-center justify-center
              shadow-md border-b border-blue-400
          "
          >
          <div className="flex flex-row gap-4 items-center justify-center">
              <div className="flex flex-col items-center mt-2 p-4">
                  <h1 className="text-xl text-yellow-200 mt-1">My Think5Whys Pocket for AI Counseing</h1>
              </div>
          </div>
      </header>
      
      <div className="mx-auto max-w-[760px] bg-black">
        <div className="list-container p-4 mt-4 bg-black">
          {loading ? (
            <Loader />
          ) : (
            <div className='w-full bg-black'>
              <div className="flex flex-row justify-between items-center ">
                <div>
                 <h3 className='text-xl lg:text-2xl mt-1 mx-2 text-white'><FontAwesomeIcon icon={faWallet} className="text-red-500 text-4xl mr-2" />My Pocket Transactions</h3>
                </div>
                <div className='flex flex-row mt-4'>
                  <button
                    className='bg-purple-700'
                    onClick={() => handleTopupClick(userId)}
                    style={{ maxWidth: '150px' }}
                  >
                    Refill
                  </button>
                  <button
                    className='bg-purple-700 ml-4'
                    onClick={handleCancel}
                    style={{ maxWidth: '150px' }}
                  >
                    Back
                  </button>
                </div>
              </div>
              <div className="mb-6 p-4 bg-green-700 rounded-lg shadow-md">
                <h3 className="text-lg font-medium">
                  Balance Amount: <span className="text-yellow-400">₹{pocketBalanceAmount.toFixed(2)}</span> 
                </h3>
              </div>
              <div className='bg-black'>
                {pocketData && pocketData.length === 0 ? (
                  // <div className='flex items-center justify-center text-yellow-300'>
                  <div className="flex items-center justify-center text-yellow-300 text-lg h-64">
                    No records found!
                  </div>
                ) : (
                  <>
                    {sortedPocketData.map((pocketItem, index) => (
                      <div key={pocketItem._id} className="bg-gray-600 my-4 p-4 text-white rounded-lg shadow-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-white">
                            {index + 1}. {formatDateTime(pocketItem.dateTransaction)}{' '} {pocketItem.currency} {' '}
                            {pocketItem.inOut !== 1 ? (
                              <span className="text-yellow-200">(-{pocketItem.amount.toFixed(2)})</span>
                            ) : (
                              <span className="text-gray-100">+{pocketItem.amount.toFixed(2)}</span>
                            )}
                          </span>
                          <Tooltip text="View details" position="top">
                            <span className="text-white cursor-pointer" onClick={() => handlePocketView(pocketItem._id)}>
                              <i className="fa-solid fa-eye"></i>
                            </span>
                          </Tooltip>
                        </div>
                        {selectedPocketId === pocketItem._id && (
                          <div className="bg-gray-600 p-4 text-white rounded-lg space-y-2 shadow-inner">
                            <p className="text-sm lg:text-lg"><strong>Date:</strong> {formatDateTime(pocketItem.dateTransaction)}</p>
                            <p className="text-sm lg:text-lg"><strong>Particulars:</strong> {pocketItem.particulars}</p>
                            <p className="text-sm lg:text-lg">
                              <strong>Amount:</strong> {pocketItem.currency} {pocketItem.amount.toFixed(2)} {pocketItem.inOut === 1 ? 'Credit' : 'Debit'}
                            </p>
                            <p className="text-sm lg:text-lg"><strong>Remarks:</strong> {pocketItem.remarks}</p>
                            <p className="text-sm lg:text-lg"><strong>Mode:</strong> {pocketItem.modePayment}</p>
                            <p className="text-sm lg:text-lg"><strong>Transaction ID:</strong> {pocketItem.transactionId}</p>
                            <p className="text-sm lg:text-lg"><strong>Payment Details:</strong> {pocketItem.paymentDetails}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <footer className="sticky bg-black border-t border-blue-400 text-white py-2 px-4 mt-2">
          <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left space-y-4 md:space-y-0">
              <p className="text-xs sm:text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions.</p>
              <div className="flex flex-row gap-2 sm:gap-6 text-xs sm:text-sm">
              <Link to="/about-us" className="text-white hover:text-purple-200 transition">About</Link>
              <Link to="/privacy-policy" className="text-white hover:text-purple-200 transition">Privacy Policy</Link>
              <Link to="/terms-of-use" className="text-white hover:text-purple-200 transition">Terms of Use</Link>
              <Link to="/contact-us" className="text-white hover:text-purple-200 transition">Contact</Link>
              </div>
          </div>
      </footer>        
    </div>
  );
};

export default MyPocket;




